import { AlertCircle, Loader2, Plus } from "lucide-react";

import { useCreatePanneau } from "../api/queries";
import type { Panneau, Proposal } from "../api/types";

interface Props {
  proposals: Proposal[];
  panneaux: Panneau[];
  onDismiss: () => void;
}

interface OrphanGroup {
  /** What ties the orphans together (e.g. `client = "Stan"`). */
  filter: { kind: "client"; value: string };
  /** Suggested panneau spec for the one-click CTA. */
  suggested: { name: string; kind: "commande_sortante"; config: Record<string, unknown> };
  count: number;
}

/**
 * Collapse a list of new proposals into orphan groups — proposals
 * whose `client` doesn't match any open panneau's filter. Each group
 * gets one CTA button.
 *
 * v1 only handles `commande_line` proposals (commande_sortante
 * panneaux). Inventory movements are always shown by inventaire
 * panneaux today (they don't need a per-product filter to surface),
 * so they're not "orphans" in the same sense.
 */
function groupOrphans(
  proposals: Proposal[],
  panneaux: Panneau[],
): OrphanGroup[] {
  const knownClients = new Set<string>();
  for (const p of panneaux) {
    if (p.kind !== "commande_sortante") continue;
    const c = p.config?.client;
    if (typeof c === "string" && c.trim()) {
      knownClients.add(c.trim().toLowerCase());
    }
  }
  const byClient = new Map<string, OrphanGroup>();
  for (const prop of proposals) {
    if (prop.type !== "commande_line") continue;
    const client = prop.payload?.client;
    if (typeof client !== "string" || !client.trim()) continue;
    if (knownClients.has(client.trim().toLowerCase())) continue;
    const key = client.trim();
    const existing = byClient.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      byClient.set(key, {
        filter: { kind: "client", value: key },
        suggested: {
          name: key,
          kind: "commande_sortante",
          config: { client: key },
        },
        count: 1,
      });
    }
  }
  return [...byClient.values()];
}

export function OrphanProposalsCTA({ proposals, panneaux, onDismiss }: Props) {
  const create = useCreatePanneau();
  const groups = groupOrphans(proposals, panneaux);
  if (groups.length === 0) return null;

  // Constrain height when many orphan groups stack: scroll within
  // the banner instead of pushing the panneaux grid down.
  const tooMany = groups.length > 3;

  return (
    <div className="max-w-6xl mx-auto mt-2 mb-1 px-4">
      {/* Banner header — explicit "!" badge ensures the message is
          read as actionable, not informational. Stronger fill
          (chip-orange/20 + chip-orange ring) gives the surface its
          due visual weight relative to the LastReplyCard above. */}
      <div className="rounded-xl bg-chip-orange/15 ring-1 ring-chip-orange/40 overflow-hidden">
        <header className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-ink dark:text-ink-dark border-b border-chip-orange/20">
          <AlertCircle className="h-3.5 w-3.5 text-chip-orange shrink-0" />
          <span>
            {groups.length === 1
              ? "1 proposition n'est rattachée à aucun panneau"
              : `${groups.length} propositions ne sont rattachées à aucun panneau`}
          </span>
        </header>
        <div
          className={`divide-y divide-chip-orange/20 ${
            tooMany ? "max-h-56 overflow-y-auto" : ""
          }`}
        >
          {groups.map((g) => (
            <div
              key={g.filter.value}
              className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm"
            >
              <span className="text-ink dark:text-ink-dark">
                <strong>{g.count}</strong>{" "}
                {g.count === 1 ? "proposition" : "propositions"} pour{" "}
                <strong>« {g.filter.value} »</strong>
              </span>
              <div className="flex-1" />
              <button
                type="button"
                disabled={create.isPending}
                onClick={async () => {
                  await create.mutateAsync(g.suggested);
                  onDismiss();
                }}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-ink text-white text-xs font-semibold hover:bg-ink/90 disabled:opacity-50"
              >
                {create.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Plus className="h-3 w-3" />
                )}
                Créer un panneau « {g.filter.value} »
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
